import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Heart, Music, Utensils, Palette, BookOpen, 
  Users, Stethoscope, Wrench, ArrowRight, Crown, 
  Baby, Home, Sparkles, Moon, Sun, Star, Gift, Eye
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';

const RoomOfGifts = ({ thresholdData, onGiftSelected, onNavigateBack }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [maryDialogue, setMaryDialogue] = useState('');
  const [showGifts, setShowGifts] = useState(false);
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [showContinue, setShowContinue] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [userInputs, setUserInputs] = useState([]);
  const [showTerminal, setShowTerminal] = useState(false);
  const [burnEffect, setBurnEffect] = useState(0);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Delayed terminal burn-through effect
  useEffect(() => {
    const terminalTimer = setTimeout(() => {
      setShowTerminal(true);
      
      // Gradual burn-through effect
      let intensity = 0;
      const burnInterval = setInterval(() => {
        setBurnEffect(intensity);
        intensity += 0.1;
        if (intensity >= 1) {
          clearInterval(burnInterval);
        }
      }, 100);
    }, 3000); // 3 second delay
    
    return () => clearTimeout(terminalTimer);
  }, []);

  // M.ai.ry gift dialogue - Faster terminal style
  useEffect(() => {
    const dialogue = `Detecting ${thresholdData.selectedThresholds?.length || 1} family transitions. What gifts do you bring? Select all that apply:`;
    
    let index = 0;
    const typeWriter = setInterval(() => {
      setMaryDialogue(dialogue.slice(0, index));
      index++;
      if (index > dialogue.length) {
        clearInterval(typeWriter);
        setTimeout(() => setShowGifts(true), 300); // Much faster
      }
    }, 25); // Very fast typing
    
    return () => clearInterval(typeWriter);
  }, [thresholdData]);

  const gifts = [
    { id: 'comfort', name: 'Comfort', icon: Heart, color: 'rose', description: 'Presence and emotional support' },
    { id: 'music', name: 'Music', icon: Music, color: 'purple', description: 'Songs that heal and connect' },
    { id: 'nourishment', name: 'Nourishment', icon: Utensils, color: 'orange', description: 'Food and sustenance for body and soul' },
    { id: 'creativity', name: 'Creativity', icon: Palette, color: 'pink', description: 'Art, beauty, and creative expression' },
    { id: 'wisdom', name: 'Wisdom', icon: BookOpen, color: 'yellow', description: 'Knowledge and guidance from experience' },
    { id: 'community', name: 'Community', icon: Users, color: 'green', description: 'Bringing people together in belonging' },
    { id: 'healing', name: 'Healing', icon: Stethoscope, color: 'emerald', description: 'Physical, emotional, or spiritual restoration' },
    { id: 'craft', name: 'Craft', icon: Wrench, color: 'blue', description: 'Building, making, creating with your hands' },
    { id: 'teaching', name: 'Teaching', icon: BookOpen, color: 'cyan', description: 'Sharing knowledge and guiding growth' },
    { id: 'storytelling', name: 'Storytelling', icon: Eye, color: 'indigo', description: 'Preserving and sharing family narratives' },
    { id: 'ritual', name: 'Ritual', icon: Star, color: 'violet', description: 'Creating sacred ceremonies and traditions' },
    { id: 'technology', name: 'Technology', icon: Wrench, color: 'gray', description: 'Digital tools and technical support' },
    { id: 'finance', name: 'Financial Wisdom', icon: Star, color: 'amber', description: 'Money management and abundance mindset' },
    { id: 'organization', name: 'Organization', icon: Users, color: 'teal', description: 'Systems and structure for family harmony' },
    { id: 'something_else', name: 'Something Else', icon: Sparkles, color: 'rainbow', description: 'Your unique gift that defies categorization' }
  ];

  const handleGiftToggle = (gift) => {
    setSelectedGifts(prev => {
      const isSelected = prev.some(g => g.id === gift.id);
      if (isSelected) {
        return prev.filter(g => g.id !== gift.id);
      } else {
        return [...prev, gift];
      }
    });
    setShowContinue(true);
  };

  const handleContinue = () => {
    const primaryGift = selectedGifts[0] || gifts[0];
    onGiftSelected({
      ...thresholdData,
      gift: primaryGift.id,
      giftName: primaryGift.name,
      giftDescription: primaryGift.description,
      selectedGifts: selectedGifts
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Dynamic Threshold Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${thresholdData.background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Grant Spiral Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-25" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(15) rotate(${spiralPhase * 0.6}deg)`,
            filter: 'blur(4px) brightness(2.0) saturate(1.5)',
            mixBlendMode: 'soft-light'
          }}
        >
          <GrantitudeSpiral size={200} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60" />

      <div className="relative z-10 flex flex-col min-h-screen p-8">
        
        {/* Room Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-5xl font-bold text-yellow-200 mb-4"
            style={{
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 40px rgba(255, 215, 0, 0.8)'
            }}
          >
            🎁 The Room of Gifts
          </h1>
          
          <p className="text-yellow-300 text-lg">
            Threshold: {thresholdData.name} • Path: {thresholdData.userType === 'giveth' ? 'Giver' : 'Seeker'}
          </p>
        </div>

        {/* M.ai.ry Terminal - Mobile-First Design */}
        {showTerminal && (
          <div 
            className="fixed inset-x-2 bottom-2 top-auto md:bottom-8 md:left-8 md:right-auto md:top-auto md:max-w-sm z-50 transition-all duration-1000"
            style={{
              top: '25vh', // Leave more space above
              bottom: '25vh', // Leave more space below  
              opacity: burnEffect,
              transform: `scale(${0.95 + burnEffect * 0.05})` // Minimal scale
            }}
          >
            <div 
              className="bg-black/98 backdrop-blur-xl border-2 border-pink-500 rounded-lg shadow-xl h-full flex flex-col"
              style={{
                boxShadow: '0 0 20px #ff1493, inset 0 0 8px #ff149320',
                filter: 'drop-shadow(0 0 8px #ff1493)',
                maxHeight: '50vh' // Terminal constrained to 50% viewport height
              }}
            >
              {/* Sticky Terminal Header */}
              <div className="flex items-center justify-between p-3 border-b border-pink-500/30 bg-black/95 shrink-0">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 bg-pink-500 rounded-full animate-pulse"></div>
                  <span className="text-pink-400 text-xs">M.ai.ry@gifts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      if (window.handleNavigateToGuildHaus) {
                        window.handleNavigateToGuildHaus();
                      }
                    }}
                    className="text-yellow-400 hover:text-yellow-300 text-xs animate-pulse px-2 py-1 bg-yellow-400/20 rounded"
                  >
                    🏰 Dashboard
                  </button>
                  <button
                    onClick={() => setShowTerminal(false)}
                    className="text-pink-400 hover:text-pink-300 text-sm w-6 h-6 flex items-center justify-center rounded hover:bg-pink-500/20"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-3 font-mono">
                <p 
                  className="text-pink-400 leading-relaxed text-xs md:text-sm mb-3"
                  style={{ 
                    fontFamily: 'Courier New, monospace',
                    textShadow: '0 0 8px #ff1493'
                  }}
                >
                  {maryDialogue}
                  {maryDialogue.length < 100 && <span className="animate-pulse">█</span>}
                </p>
                
                {/* User Input Box */}
                <div className="border border-pink-400/50 bg-black/50 rounded p-2 mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-pink-400 text-xs">you:</span>
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userInput.trim()) {
                          // Soul score chime for gift input
                          try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            const o = ctx.createOscillator();
                            const g = ctx.createGain();
                            o.type = "sine";
                            o.frequency.value = 741;
                            o.connect(g);
                            g.connect(ctx.destination);
                            g.gain.setValueAtTime(0.15, ctx.currentTime);
                            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
                            o.start();
                            o.stop(ctx.currentTime + 0.4);
                          } catch (e) {}
                          
                          const response = `"${userInput}" - Your authentic expression deepens my understanding of your gifts.`;
                          setMaryDialogue(prev => prev + `\n\n> ${userInput}\n< ${response}`);
                          setUserInputs(prev => [...prev, { content: userInput, timestamp: new Date(), context: 'gift_reflection' }]);
                          setUserInput('');
                        }
                      }}
                      placeholder="Share about your gifts..."
                      className="flex-1 bg-transparent text-white text-xs outline-none"
                    />
                    <span className="text-pink-400 animate-pulse text-xs">█</span>
                  </div>
                </div>
                
                {/* Gift Selection - Compact */}
                {showGifts && (
                  <div className="space-y-2">
                    <div className="text-pink-300 text-xs font-bold">SELECT GIFTS:</div>
                    <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                      {gifts.map(gift => {
                        const IconComponent = gift.icon;
                        const isSelected = selectedGifts.some(g => g.id === gift.id);
                        
                        return (
                          <button
                            key={gift.id}
                            onClick={() => handleGiftToggle(gift)}
                            className={`p-2 rounded text-xs border transition-all ${
                              isSelected
                                ? 'bg-pink-500/30 border-pink-400 text-pink-200'
                                : 'bg-black/30 border-pink-500/30 text-pink-300 hover:bg-pink-500/20'
                            }`}
                          >
                            <div className="flex items-center space-x-1">
                              <IconComponent className="w-3 h-3" />
                              <span className="text-xs leading-tight">{gift.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sticky Bottom Actions */}
              {showContinue && selectedGifts.length > 0 && (
                <div className="p-3 border-t border-pink-500/30 bg-black/95 shrink-0">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white text-sm py-3 rounded font-medium"
                  >
                    Continue with {selectedGifts.length} gift{selectedGifts.length > 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gift Selection - Multi-Select with Continue */}
        {showGifts && (
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {gifts.map((gift) => {
                const IconComponent = gift.icon;
                const isSelected = selectedGifts.some(g => g.id === gift.id);
                
                return (
                  <button
                    key={gift.id}
                    onClick={() => handleGiftToggle(gift)}
                    className={`group relative overflow-hidden rounded-xl border-2 p-4 transition-all duration-300 ${
                      isSelected 
                        ? `border-${gift.color}-400 bg-${gift.color}-900/60 scale-105` 
                        : `border-${gift.color}-400/50 bg-${gift.color}-900/20 hover:bg-${gift.color}-900/40`
                    }`}
                  >
                    <div className="relative z-10 text-center space-y-3">
                      <div className={`w-12 h-12 mx-auto bg-${gift.color}-500/40 rounded-full flex items-center justify-center`}>
                        <IconComponent className={`w-6 h-6 text-${gift.color}-200`} />
                      </div>
                      
                      <h3 className={`text-sm font-bold text-${gift.color}-200`}>
                        {gift.name}
                      </h3>
                      
                      <p className={`text-${gift.color}-300 text-xs leading-relaxed`}>
                        {gift.description}
                      </p>
                      
                      <div className={`w-6 h-6 mx-auto bg-${gift.color}-400/30 rounded-full flex items-center justify-center`}>
                        <span className="text-white text-xs">{isSelected ? '✓' : '✨'}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Continue Button */}
            {showContinue && selectedGifts.length > 0 && (
              <div className="text-center">
                <button
                  onClick={handleContinue}
                  className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 20, 147, 0.4)'
                  }}
                >
                  Continue with {selectedGifts.length} gift{selectedGifts.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Selection Confirmation */}
        {selectedGifts.length > 0 && !showContinue && (
          <div className="text-center">
            <div className="inline-block bg-yellow-500/20 backdrop-blur-xl border border-yellow-400/30 rounded-2xl p-8">
              <div className="animate-pulse">
                <Crown className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
                <p className="text-yellow-200 text-lg">
                  Processing {selectedGifts.length} gift{selectedGifts.length > 1 ? 's' : ''}...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="absolute top-4 left-4">
          <button
            onClick={onNavigateBack}
            disabled={selectedGifts.length > 0 && !showContinue}
            className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full p-3 hover:bg-black/60 transition-all disabled:opacity-50"
          >
            <ArrowRight className="w-5 h-5 text-white transform rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomOfGifts;